import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';

import SystemService from '../../../../data/system-service';
import Auth from '../../../../data/auth-service';
import DataContext from '../../../../data/data-context';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    fullWidth: {
        width: '100%',
    },
    fullWidthInput: {
        marginTop: '16px',
        width: '100%',
        '& div.MuiInputBase-root': {
            width: '100%',
        }
    },
    labelScale: {
        fontSize: '0.750rem',
    },
    panel: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
    },
}));

const AWSCredentials = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, setSystemSettings, updateSystemSettings } = useContext(DataContext);
    const [changes, setChanges] = useState(false);
    const [aws, setAWS] = useState({
        as_conf: {
            enabled: false,
            groups_mon_freq: 2,
            region: '',
            stats_mon_freq: 15,
            user: '',
        },
        user_creds: {
            access_key_id: '',
            secret_access_key: '',
        }
    });
    const awsForm = useValidate({});
    const awsCredsForm = useValidate({});
    const [disableVerify, setDisableVerify] = useState(false);
    const [kick] = useState(false);
    const [disabled, setDisabled] = useState(false)
    const awsPerms = Auth.permissions.get('AWS');

    useEffect(() => {
        if (systemSettings && systemSettings.aws) {
            const isChange = !_isEqual(aws, systemSettings.aws);
            if (changes !== isChange) {
                setChanges(!changes);
            }
        }
    }, [aws]);

    useEffect(() => {
        const valid = ((aws.as_conf.user || '').length > 0 &&
            (aws.as_conf.region || '').length > 0 &&
            (aws.user_creds.access_key_id || '').length > 0 &&
            (aws.user_creds.secret_access_key || '').length > 0 &&
            checkValidity(awsCredsForm.get()));
        setDisabled(valid);
        setDisableVerify(valid);
    }, [aws, awsCredsForm]);

    useEffect(() => {
        if (systemSettings && systemSettings.aws) {
            // Check for changes here too to stop unnecessary renders before the change flag is set
            const isChange = !_isEqual(aws, systemSettings.aws);
            if (isChange) {
                const a = _cloneDeep(systemSettings.aws);
                setAWS(a);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [systemSettings]);

    const handleEnable = (e) => {
        const a = { ...aws };
        a.as_conf.enabled = e.target.checked;
        setAWS(a);
    };

    const handleRegion = (e, validID, valid) => {
        awsForm.set({ ...awsForm.get(), [validID]: valid });
        awsCredsForm.set({ ...awsCredsForm.get(), [validID]: valid });

        const a = { ...aws };
        a.as_conf.region = e.target.value;
        setAWS(a);
    };

    const handleUser = (e, validID, valid) => {
        awsForm.set({ ...awsForm.get(), [validID]: valid });
        awsCredsForm.set({ ...awsCredsForm.get(), [validID]: valid });

        const a = { ...aws };
        a.as_conf.user = e.target.value;
        setAWS(a);
    };

    const handleAccessKey = (e, validID, valid) => {
        awsForm.set({ ...awsForm.get(), [validID]: valid });
        awsCredsForm.set({ ...awsCredsForm.get(), [validID]: valid });

        const a = { ...aws };
        a.user_creds.access_key_id = e.target.value;
        setAWS(a);
    };

    const handleSecretKey = (e, validID, valid) => {
        awsForm.set({ ...awsForm.get(), [validID]: valid });
        awsCredsForm.set({ ...awsCredsForm.get(), [validID]: valid });

        const a = { ...aws };
        a.user_creds.secret_access_key = e.target.value;
        setAWS(a);
    };

    const handleStatsFrequency = (e, validID, valid) => {
        awsForm.set({ ...awsForm.get(), [validID]: valid });
        const a = { ...aws };
        a.as_conf.stats_mon_freq = parseInt(e.target.value);
        setAWS(a);
    };

    const handleGroupsFrequency = (e, validID, valid) => {
        awsForm.set({ ...awsForm.get(), [validID]: valid });
        const a = { ...aws };
        a.as_conf.groups_mon_freq = parseInt(e.target.value);
        setAWS(a);
    };

    const handleVerify = () => {
        setDisableVerify(true);
        SystemService.awsConfigVerify(aws).then((r) => {
            toast.success('Verification successful');
            setDisableVerify(false);
        }, err => {
            setDisableVerify(false);
            if (err.message && err.error) {
                toast.error(`${err.message}: ${err.error}`);
            } else {
                toast.error(err.message);
            }
        });
    };

    const handleSave = () => {
        handleCancel();
        setTimeout(() => {
            setSystemSettings(_cloneDeep({ aws }));
            updateSystemSettings(_cloneDeep({ aws }));
        });
    };

    const handleCancel = () => {
        navigate('/system/port_mirroring/aws');
    };

    return (
        <Paper elevation={3}>
            <Box p={5}>
                <Grid container spacing={5}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            style={{
                                marginLeft: '0px',
                            }}
                            disabled={!awsPerms.admin}
                            label="Enable"
                            labelPlacement="start"
                            control={
                                <Checkbox
                                    checked={aws.as_conf.enabled}
                                    onChange={handleEnable}
                                />
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h5">AWS Credentials</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Box>
                            <Validator
                                errorText="Region cannot be empty"
                                label="Region"
                                disabled={!awsPerms.write || !aws.as_conf.enabled}
                                fullWidth
                                value={aws.as_conf.region}
                                kick={kick}
                                onChange={handleRegion}
                            />
                        </Box>
                        <Box mt={3}>
                            <Validator
                                errorText="User cannot be empty"
                                label="User"
                                disabled={!awsPerms.write || !aws.as_conf.enabled}
                                fullWidth
                                value={aws.as_conf.user}
                                kick={kick}
                                onChange={handleUser}
                            />
                        </Box>
                        <Box mt={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Validator
                                        valueType="range"
                                        errorText="Amazon CloudWatch stats frequency must be within 15 and 60 minutes"
                                        type="number"
                                        fullWidth
                                        inputProps={{
                                            min: 15,
                                            max: 60,
                                        }}
                                        label=" Amazon CloudWatch Stats Frequency"
                                        value={aws.as_conf.stats_mon_freq}
                                        onChange={handleStatsFrequency}
                                        endAdornment={<InputAdornment position="end">Minutes</InputAdornment>}
                                        disabled={!awsPerms.write || !aws.as_conf.enabled}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Validator
                                        valueType="range"
                                        errorText="Auto Scaling Group frequency must be within 2 and 60 minutes"
                                        type="number"
                                        fullWidth
                                        inputProps={{
                                            min: 2,
                                            max: 60,
                                        }}
                                        label="Auto Scaling Group Frequency"
                                        value={aws.as_conf.groups_mon_freq}
                                        onChange={handleGroupsFrequency}
                                        endAdornment={<InputAdornment position="end">Minutes</InputAdornment>}
                                        disabled={!awsPerms.write || !aws.as_conf.enabled}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box>
                            <Validator
                                errorText="Access key ID cannot be empty"
                                label="Access Key ID"
                                disabled={!awsPerms.write || !aws.as_conf.enabled}
                                fullWidth
                                value={aws.user_creds.access_key_id}
                                kick={kick}
                                onChange={handleAccessKey}
                            />
                        </Box>
                        <Box mt={3}>
                            <Validator
                                errorText="Secret access key cannot be empty"
                                label="Secret Access Key"
                                disabled={!awsPerms.write || !aws.as_conf.enabled}
                                fullWidth
                                type="password"
                                value={aws.user_creds.secret_access_key}
                                kick={kick}
                                onChange={handleSecretKey}
                            />
                        </Box>
                        <Box mt={3}>
                            <Button
                                className={classes.buttonRight}
                                variant="contained"
                                color="primary"
                                aria-label="Verify Credentials"
                                onClick={handleVerify}
                                disabled={!aws.as_conf.enabled || !checkValidity(awsCredsForm) || !disableVerify}
                            >
                                Verify Credentials
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12} container justifyContent="flex-end">
                        <Box mr={2}>
                            <Button variant="contained" color="secondary" aria-label="Cancel" onClick={handleCancel}>Cancel</Button>
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            aria-label="Save"
                            onClick={handleSave}
                            disabled={!changes || !(checkValidity(awsCredsForm.get())) || !disabled}
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
};

export default AWSCredentials;
